import React, { useState, useEffect } from 'react'
import { Play, BookOpen, Users, Star, Clock, DollarSign, Filter, Search, Plus, Eye, Heart, Share2 } from 'lucide-react'
import { GuruContent, GuruConnectApi } from '../../services/guruConnectApi'

interface ContentMarketplaceProps {
  userRole: 'client' | 'guru' | 'admin'
  currentUserId?: string
}

export const ContentMarketplace: React.FC<ContentMarketplaceProps> = ({ userRole, currentUserId }) => {
  const [content, setContent] = useState<GuruContent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterPrice, setFilterPrice] = useState<string>('all')
  const [sortBy, setSortBy] = useState<string>('newest')
  const [selectedContent, setSelectedContent] = useState<GuruContent | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)

  useEffect(() => {
    loadContent()
  }, [filterType, filterPrice, sortBy])

  const loadContent = async () => {
    try {
      setLoading(true)
      setError('')
      const result = await GuruConnectApi.searchContent({
        content_type: filterType !== 'all' ? filterType : undefined,
        max_price: filterPrice !== 'all' ? parseFloat(filterPrice) : undefined,
        is_published: true,
        search_term: searchQuery || undefined,
        limit: 50
      })
      setContent(result.content)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    loadContent()
  }

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Play className="w-5 h-5" />
      case 'article':
        return <BookOpen className="w-5 h-5" />
      case 'course':
        return <Users className="w-5 h-5" />
      case 'workshop':
        return <Users className="w-5 h-5" />
      case 'live_stream':
        return <Play className="w-5 h-5" />
      case 'podcast':
        return <Play className="w-5 h-5" />
      default:
        return <BookOpen className="w-5 h-5" />
    }
  }

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case 'video':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'article':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'course':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'workshop':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'live_stream':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'podcast':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const handlePurchase = async (contentId: string, price: number) => {
    // In a real app, this would integrate with Stripe for payment processing
    console.log('Purchasing content:', contentId, 'for $', price)
    // For now, just show a success message
    alert('Purchase successful! Content will be available in your library.')
  }

  const handleLike = async (contentId: string) => {
    // In a real app, this would update the like count
    console.log('Liking content:', contentId)
  }

  const handleShare = async (contentId: string) => {
    // In a real app, this would share the content
    console.log('Sharing content:', contentId)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this health content',
          text: 'I found this great health content on ZippyHealth',
          url: window.location.href
        })
      } catch (err) {
        console.log('Share cancelled')
      }
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(window.location.href)
      alert('Link copied to clipboard!')
    }
  }

  const sortedContent = [...content].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      case 'oldest':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      case 'price_low':
        return a.price - b.price
      case 'price_high':
        return b.price - a.price
      case 'popular':
        return b.view_count - a.view_count
      case 'rating':
        return b.like_count - a.like_count
      default:
        return 0
    }
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading content...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Health Content Marketplace</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Discover expert health content from verified professionals
          </p>
        </div>
        
        {userRole === 'guru' && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Content</span>
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Types</option>
            <option value="article">Articles</option>
            <option value="video">Videos</option>
            <option value="course">Courses</option>
            <option value="workshop">Workshops</option>
            <option value="live_stream">Live Streams</option>
            <option value="podcast">Podcasts</option>
          </select>
          
          <select
            value={filterPrice}
            onChange={(e) => setFilterPrice(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Prices</option>
            <option value="0">Free</option>
            <option value="10">Under $10</option>
            <option value="25">Under $25</option>
            <option value="50">Under $50</option>
            <option value="100">Under $100</option>
          </select>
        </div>
        
        <div className="flex justify-between items-center mt-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border-none bg-transparent text-gray-900 dark:text-white focus:ring-0"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="popular">Most Popular</option>
              <option value="rating">Highest Rated</option>
            </select>
          </div>
          
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {sortedContent.length} content items
          </span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Content Grid */}
      {sortedContent.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Content Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search criteria or check back later for new content.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {sortedContent.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedContent(item)}
            >
              {/* Thumbnail */}
              <div className="relative h-48 bg-gradient-to-br from-blue-400 to-purple-500">
                {item.thumbnail_url ? (
                  <img
                    src={item.thumbnail_url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {getContentTypeIcon(item.content_type)}
                  </div>
                )}
                
                <div className="absolute top-3 left-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getContentTypeColor(item.content_type)}`}>
                    {GuruConnectApi.getContentTypeDisplay(item.content_type)}
                  </span>
                </div>
                
                <div className="absolute top-3 right-3 flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleLike(item.id)
                    }}
                    className="p-1 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                  >
                    <Heart className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleShare(item.id)
                    }}
                    className="p-1 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Content Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                  {item.title}
                </h3>
                
                {item.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                    {item.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-1">
                      <Eye className="w-4 h-4" />
                      <span>{item.view_count}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Heart className="w-4 h-4" />
                      <span>{item.like_count}</span>
                    </div>
                    {item.duration_minutes && (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{item.duration_minutes}m</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-green-600">
                      {item.price === 0 ? 'Free' : GuruConnectApi.formatPrice(item.price)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                      {item.guru_profiles?.users?.first_name?.[0] || 'G'}
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {item.guru_profiles ? GuruConnectApi.getGuruDisplayName(item.guru_profiles) : 'Unknown Guru'}
                    </span>
                  </div>
                  
                  {item.price > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePurchase(item.id, item.price)
                      }}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors"
                    >
                      Purchase
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content Detail Modal */}
      {selectedContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedContent.title}
                </h2>
                <button
                  onClick={() => setSelectedContent(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Content Preview */}
                <div>
                  <div className="relative h-64 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg mb-4">
                    {selectedContent.thumbnail_url ? (
                      <img
                        src={selectedContent.thumbnail_url}
                        alt={selectedContent.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center rounded-lg">
                        {getContentTypeIcon(selectedContent.content_type)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getContentTypeColor(selectedContent.content_type)}`}>
                      {GuruConnectApi.getContentTypeDisplay(selectedContent.content_type)}
                    </span>
                    <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                      <Eye className="w-4 h-4" />
                      <span className="text-sm">{selectedContent.view_count} views</span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                      <Heart className="w-4 h-4" />
                      <span className="text-sm">{selectedContent.like_count} likes</span>
                    </div>
                  </div>
                </div>
                
                {/* Content Details */}
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Description</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {selectedContent.description || 'No description available.'}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Duration:</span>
                      <p className="font-medium">
                        {selectedContent.duration_minutes ? `${selectedContent.duration_minutes} minutes` : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Price:</span>
                      <p className="font-medium text-green-600">
                        {selectedContent.price === 0 ? 'Free' : GuruConnectApi.formatPrice(selectedContent.price)}
                      </p>
                    </div>
                  </div>
                  
                  {selectedContent.tags.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedContent.tags.map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-3 pt-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {selectedContent.guru_profiles?.users?.first_name?.[0] || 'G'}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {selectedContent.guru_profiles ? GuruConnectApi.getGuruDisplayName(selectedContent.guru_profiles) : 'Unknown Guru'}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedContent.guru_profiles ? GuruConnectApi.getGuruRoleDisplay(selectedContent.guru_profiles.guru_role) : 'Health Professional'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    {selectedContent.price > 0 ? (
                      <button
                        onClick={() => handlePurchase(selectedContent.id, selectedContent.price)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                      >
                        Purchase ({GuruConnectApi.formatPrice(selectedContent.price)})
                      </button>
                    ) : (
                      <button className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors">
                        Access Free Content
                      </button>
                    )}
                    <button
                      onClick={() => handleShare(selectedContent.id)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Share
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 